import React, { useMemo, useCallback, useId } from 'react';
import { useTheme } from '@mui/material';
import { Area } from 'recharts';
import { ResponsiveContainer, AreaChart as RechartsAreaChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import {
  formatValue,
  getLineColor,
  getLegendConfig,
  getYAxisDomain,
  processTimeseriesData,
  makeAxisFormatter,
} from './chartUtils';
import { parseThresholds } from './thresholdUtils';
import WidgetState from './WidgetState';

/**
 * Area Chart Widget - Stacked/overlapping area chart
 * Fork of LineChart with Area components and gradient fills
 */
const AreaChartWidget = ({ data, config = {} }) => {
  // SVG element ids are DOCUMENT-global, so a fixed `areaGrad-${index}` meant
  // two area widgets on the same dashboard both defined `areaGrad-0` and every
  // url(#...) reference resolved to whichever mounted first — widget B silently
  // rendered widget A's fill. useId gives a per-instance namespace.
  // useId() embeds colons (":r0:"); strip them so the id stays a plain
  // token in every context that consumes it.
  const gradientPrefix = `areaGrad${useId().replace(/:/g, '')}`;
  const theme = useTheme();

  const timeseriesData = useMemo(() => {
    if (!data || !data.data || data.type !== 'timeseries') {
      return null;
    }
    return processTimeseriesData(data.data, config.legendTemplate);
  }, [data, config.legendTemplate]);

  const legendConfig = useMemo(() => getLegendConfig(config), [
    config
  ]);

  const yAxisDomain = useMemo(() => getYAxisDomain(config), [
    config
  ]);

  const yAxisLabel = useMemo(() =>
    config.yAxisLabel ? { value: config.yAxisLabel, angle: -90, position: 'insideLeft' } : undefined,
    [config.yAxisLabel]
  );

  // Timestamps are already in milliseconds from processTimeseriesData
  const xAxisTickFormatter = useCallback(
    (timestamp) => new Date(timestamp).toLocaleTimeString(),
    []
  );

  const yAxisTickFormatter = useMemo(
    () => makeAxisFormatter(config.unit, config.decimalPlaces),
    [config.unit, config.decimalPlaces]
  );

  const thresholds = useMemo(() => parseThresholds(config.thresholdsJson), [config.thresholdsJson]);

  const tooltipLabelFormatter = useCallback(
    (timestamp) => new Date(timestamp).toLocaleString(),
    []
  );

  const tooltipFormatter = useCallback(
    (value) => [formatValue(value, config), ''],
    [config]
  );

  // Theme-aware tooltip surface — glassy dark on dark, light bg + dark text on light.
  const tooltipContentStyle = useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      backgroundColor: isDark ? 'rgba(5, 5, 5, 0.75)' : 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(12px)',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: '8px',
      boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.15)',
      color: isDark ? '#fff' : '#1a1a1a',
    };
  }, [theme.palette.mode]);

  // Memoize gradient definitions
  const gradientDefs = useMemo(() => {
    if (!timeseriesData) return null;
    return timeseriesData.series.map((series, index) => {
      const color = getLineColor(index, config.colorScheme);
      return (
        <linearGradient key={`grad-${index}`} id={`${gradientPrefix}-${index}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color} stopOpacity={0.4} />
          <stop offset="95%" stopColor={color} stopOpacity={0.0} />
        </linearGradient>
      );
    });
  }, [timeseriesData, config.colorScheme]);

  // Handle scalar/table results
  if (!timeseriesData && data) {
    let scalarValue = null;
    if (data.type === 'scalar') {
      scalarValue = parseFloat(data.data);
    } else if (data.type === 'table' && data.rows && data.rows.length > 0) {
      const numVal = Object.values(data.rows[0]).find(v => typeof v === 'number');
      if (numVal !== undefined) scalarValue = numVal;
    }

    if (scalarValue !== null) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>{formatValue(scalarValue, config)}</div>
          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 8 }}>
            Scalar result — use Stat or Gauge widget, or add GROUP BY with a time column for timeseries
          </div>
        </div>
      );
    }
    return (
      <WidgetState isEmpty />
    );
  }

  if (!timeseriesData) {
    return (
      <WidgetState isEmpty />
    );
  }

  if (!timeseriesData.dataPoints || timeseriesData.dataPoints.length === 0) {
    return (
      <WidgetState isEmpty />
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsAreaChart data={timeseriesData.dataPoints} syncId="dashboard-sync">
        <defs>
          {gradientDefs}
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="timestamp"
          type="number"
          domain={config.timeDomain || ['dataMin', 'dataMax']}
          allowDataOverflow={Boolean(config.timeDomain)}
          tickFormatter={xAxisTickFormatter}
        />
        <YAxis
          domain={yAxisDomain}
          label={yAxisLabel}
          tickFormatter={yAxisTickFormatter}
        />
        <Tooltip
          labelFormatter={tooltipLabelFormatter}
          formatter={tooltipFormatter}
          contentStyle={tooltipContentStyle}
          itemStyle={{ fontWeight: 500 }}
          cursor={{ stroke: 'rgba(128, 128, 128, 0.25)', strokeWidth: 1 }}
        />
        {legendConfig && <Legend {...legendConfig} />}
        {thresholds.map((t, i) => (
          <ReferenceLine
            key={`th-${i}`}
            y={t.value}
            stroke={t.color}
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
            label={t.label ? { value: t.label, position: 'right', fill: t.color, fontSize: 11 } : undefined}
          />
        ))}
        {timeseriesData.series.map((series, index) => (
          <Area
            key={series}
            type="monotone"
            dataKey={series}
            stroke={getLineColor(index, config.colorScheme)}
            fill={`url(#${gradientPrefix}-${index})`}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
            connectNulls={config.connectNulls || false}
            isAnimationActive={false}
          />
        ))}
      </RechartsAreaChart>
    </ResponsiveContainer>
  );
};

export default React.memo(AreaChartWidget, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.legendTemplate === nextProps.config?.legendTemplate &&
    prevProps.config?.showLegend === nextProps.config?.showLegend &&
    prevProps.config?.legendPosition === nextProps.config?.legendPosition &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator &&
    prevProps.config?.yAxisMin === nextProps.config?.yAxisMin &&
    prevProps.config?.yAxisMax === nextProps.config?.yAxisMax &&
    prevProps.config?.yAxisLabel === nextProps.config?.yAxisLabel &&
    prevProps.config?.colorScheme === nextProps.config?.colorScheme &&
    prevProps.config?.connectNulls === nextProps.config?.connectNulls &&
    prevProps.config?.thresholdsJson === nextProps.config?.thresholdsJson &&
    (prevProps.config?.timeDomain?.[0] === nextProps.config?.timeDomain?.[0] &&
     prevProps.config?.timeDomain?.[1] === nextProps.config?.timeDomain?.[1])
  );
});
